// The pure rules of the MRP screens (spec 03 §4.1, §4.5): the address of the
// planned-orders list, exact quantity text, run facts the workbench reads, the
// per-kind counts a confirmation states, the cancel fan-out and the conversion's
// split of a selection into what is sent and what is not. No React and no
// network here, so each rule is unit-tested alone.

import type { TFunction } from "i18next";
import { facilityTodayIso } from "../../../../lib/business-dates";
import {
  ACTION_MESSAGE_KINDS,
  PLANNED_ORDER_KINDS,
  PLANNED_ORDER_STATUSES,
  type ActionMessageKind,
  type ExpectedActionMessage,
  type MrpRun,
  type MrpRunList,
  type MrpRunStats,
  type ExplanationDemandItem,
  type PlannedOrder,
  type PlannedOrderBulkResult,
  type PlannedOrderConvertResult,
  type PlannedOrderConvertedKind,
  type PlannedOrderExplanation,
  type PlannedOrderFilters,
  type PlannedOrderKind,
  type PlannedOrderStatus,
} from "../hooks/mrp-queries";
import { shortId as shortIdTail } from "../../../../lib/short-id";

// ---- the list's address --------------------------------------------------------

export type PlannedOrdersView = "all" | "messages";

/** `/planning/orders` search: the open drawer, the tab and §5 row 42's filters. */
export interface PlannedOrdersSearch extends PlannedOrderFilters {
  readonly order?: string | undefined;
  readonly view?: PlannedOrdersView | undefined;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const DATE = /^\d{4}-\d{2}-\d{2}$/u;

function uuid(v: unknown): string | undefined {
  return typeof v === "string" && UUID.test(v) ? v.toLowerCase() : undefined;
}

function date(v: unknown): string | undefined {
  if (typeof v !== "string" || !DATE.test(v)) return undefined;
  const t = Date.parse(`${v}T00:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === v ? v : undefined;
}

function oneOf<T extends string>(v: unknown, allowed: readonly T[]): T | undefined {
  return typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
}

/**
 * Re-validates the address defensively: a shared link with a stale or
 * malformed parameter opens the screen with that parameter dropped, never a
 * refused read.
 */
export function validatePlannedOrdersSearch(search: Record<string, unknown>): PlannedOrdersSearch {
  const out: Record<string, string> = {};
  const set = (key: string, value: string | undefined) => {
    if (value !== undefined) out[key] = value;
  };
  set("order", uuid(search["order"]));
  set("view", oneOf(search["view"], ["all", "messages"] as const));
  set("kind", oneOf(search["kind"], PLANNED_ORDER_KINDS));
  set("status", oneOf(search["status"], PLANNED_ORDER_STATUSES));
  set("message", oneOf(search["message"], ["true", "false", ...ACTION_MESSAGE_KINDS] as const));
  set("from", date(search["from"]));
  set("to", date(search["to"]));
  set("product", uuid(search["product"]));
  const planner = search["planner"];
  if (typeof planner === "string" && planner.trim().length > 0 && planner.length <= 64)
    out["planner"] = planner.trim();
  set("run", uuid(search["run"]));
  set("line", uuid(search["line"]));
  set("entry", uuid(search["entry"]));
  return out;
}

/** The address without the keys a change cleared, so the URL carries no empty parameter. */
export function compactSearch(search: PlannedOrdersSearch): PlannedOrdersSearch {
  const out: PlannedOrdersSearch = Object.fromEntries(
    Object.entries(search).filter(([, value]) => value !== undefined),
  );
  return out;
}

/** The keys of §5 row 42 the address filters by. */
export const FILTER_KEYS = [
  "kind",
  "status",
  "message",
  "from",
  "to",
  "product",
  "planner",
  "run",
  "line",
  "entry",
] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

/**
 * The server filters the address names. The Action messages tab reads the
 * orders that carry a message, so its `message` is `true` unless the address
 * names a kind.
 */
export function filtersOf(search: PlannedOrdersSearch): PlannedOrderFilters {
  const filters: Record<string, string> = {};
  for (const key of FILTER_KEYS) {
    const value = search[key];
    if (value !== undefined) filters[key] = value;
  }
  if (search.view === "messages") {
    const kind = oneOf(search.message, ACTION_MESSAGE_KINDS);
    filters["message"] = kind ?? "true";
  }
  return filters;
}

/** The filters set on the address, in a stable order, so filtered-empty can name them. */
export function activeFilters(search: PlannedOrdersSearch): FilterKey[] {
  return FILTER_KEYS.filter((k) => search[k] !== undefined);
}

// ---- exact quantities --------------------------------------------------------------

const QTY = /^(-?)(\d+)(?:\.(\d+))?$/u;
const grouping = new Map<string, Intl.NumberFormat>();
const separators = new Map<string, string>();

function groupFormat(locale: string): Intl.NumberFormat {
  let f = grouping.get(locale);
  if (f === undefined) {
    f = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    grouping.set(locale, f);
  }
  return f;
}

function decimalSeparator(locale: string): string {
  let s = separators.get(locale);
  if (s === undefined) {
    s =
      new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === "decimal")?.value ??
      ".";
    separators.set(locale, s);
  }
  return s;
}

/**
 * A decimal string in the reader's locale, built from the digits so a
 * six-place explanation quantity is never rounded through a float (spec 03
 * §4: quantities formatted per locale, the raw value kept for copying).
 * Trailing zeros of the fraction are dropped; text that is not a decimal is
 * returned as it came.
 */
export function formatExactQty(value: string, locale: string, signed = false): string {
  const m = QTY.exec(value.trim());
  if (m === null) return value;
  const negative = m[1] === "-";
  const whole = m[2] ?? "0";
  const frac = (m[3] ?? "").replace(/0+$/u, "");
  const zero = /^0+$/u.test(whole) && frac === "";
  const body = `${groupFormat(locale).format(BigInt(whole))}${
    frac === "" ? "" : `${decimalSeparator(locale)}${frac}`
  }`;
  if (zero) return body;
  if (negative) return `−${body}`;
  return signed ? `+${body}` : body;
}

/** True when a decimal string is zero, so a waterfall term shows it was counted. */
export function isZeroQty(value: string): boolean {
  const m = QTY.exec(value.trim());
  return m !== null && /^0+$/u.test(m[2] ?? "") && /^0*$/u.test(m[3] ?? "");
}

// ---- run facts --------------------------------------------------------------------

/** Minutes and seconds between a run's start and its end, or undefined while it has no end. */
export function mrpRunDuration(
  run: Pick<MrpRun, "started_at" | "finished_at">,
): { minutes: number; seconds: number } | undefined {
  if (run.started_at === null || run.finished_at === null) return undefined;
  const ms = Date.parse(run.finished_at) - Date.parse(run.started_at);
  if (!Number.isFinite(ms) || ms < 0) return undefined;
  const total = Math.round(ms / 1000);
  return { minutes: Math.floor(total / 60), seconds: total % 60 };
}

/**
 * How long a run took, as a person says it: "1 min 12 s", "7 s", or "Under
 * 1 s" for a run that finished inside the second, where "0 min 0 s" read like
 * a broken counter. Shared by the demand refresh and the MRP run histories.
 */
export function formatRunDuration(
  t: TFunction,
  d: { readonly minutes: number; readonly seconds: number },
): string {
  if (d.minutes > 0) return t("planning.workbench.duration", d);
  if (d.seconds > 0) return t("planning.workbench.durationSeconds", { seconds: d.seconds });
  return t("planning.workbench.durationUnderSecond");
}

/**
 * A succeeded run that left products out for a graph error reads amber
 * (§4.1): a BOM cycle, a by-product cycle, a chain past twelve levels, or a
 * product cycle across facilities. Procurement is served `graph_excluded`
 * instead of the lists (§7's table).
 */
export function graphExcluded(stats: MrpRunStats): boolean {
  if (stats.graph_excluded === true) return true;
  return (
    (stats.bom_cycle_count ?? 0) > 0 ||
    (stats.byproduct_cycle_count ?? 0) > 0 ||
    (stats.depth_exceeded_count ?? 0) > 0 ||
    (stats.product_cycles_count ?? 0) > 0
  );
}

/** The run a facility's tiles describe: the newest that ended, else the newest. */
export function lastEndedRun(list: MrpRunList | undefined): MrpRun | undefined {
  if (list === undefined) return undefined;
  return list.items.find((r) => r.status === "succeeded" || r.status === "failed") ?? list.items[0];
}

/** The facility's queued run, if one waits. */
export function queuedRun(list: MrpRunList | undefined): MrpRun | undefined {
  return list?.items.find((r) => r.status === "queued");
}

/** The newest run that belongs to a group pass, which carries that pass's fixpoint. */
export function lastOrchestratedRun(list: MrpRunList | undefined): MrpRun | undefined {
  return list?.items.find((r) => r.orchestration_id !== null);
}

/**
 * Whether the nightly pass skipped this facility for having no calendar and
 * nothing to plan (spec 03 §9 item 39). The skip writes no run and counts no
 * failure, but it stamps the watermark, so the facility was skipped on the
 * watermark's date when no run of a group pass falls on or after it at the
 * facility. A page that ends before that date without a next page has read
 * every run there is; a page that does not reach that far cannot say, and
 * reads as not skipped.
 */
export function nightlySkipped(
  list: MrpRunList | undefined,
  timezone: string | null | undefined,
): boolean {
  const night = list?.nightly_mrp_on ?? null;
  if (list === undefined || night === null) return false;
  const dayOf = (iso: string) => facilityTodayIso(Date.parse(iso), timezone);
  for (const run of list.items) {
    const at = run.finished_at ?? run.started_at ?? run.due_at;
    if (run.orchestration_id !== null && dayOf(at) >= night) return false;
  }
  const oldest = list.items[list.items.length - 1];
  if (oldest === undefined) return true;
  const reachesBack = dayOf(oldest.created_at) < night;
  return reachesBack || list.next_cursor === null;
}

/** The stats lists §4.5's run detail prints, each beside its exact count, in §2.9's order. */
export const RUN_STAT_LISTS = [
  "skipped",
  "skipped_work_orders",
  "no_bom",
  "supply_limited",
  "defaulted_parameters",
  "transfer_source_inactive",
  "depth_exceeded",
  "bom_cycle",
  "byproduct_cycle",
  "product_cycles",
  "uncalendared_work_centers",
  "closed_work_centers",
  "closed_facility_windows",
  "mps_exceeded",
  "mps_unplanned",
] as const;
export type RunStatList = (typeof RUN_STAT_LISTS)[number];

/** A list's entries and its exact count; the count stands when the list is capped at 200 or withheld. */
export function statList(
  stats: MrpRunStats,
  name: RunStatList,
): { entries: readonly unknown[]; count: number } | undefined {
  const entries = (stats as Record<string, unknown>)[name];
  const count = (stats as Record<string, unknown>)[`${name}_count`];
  if (entries === undefined && count === undefined) return undefined;
  const list = Array.isArray(entries) ? (entries as unknown[]) : [];
  return { entries: list, count: typeof count === "number" ? count : list.length };
}

// ---- selections, confirmations and outcomes --------------------------------------

/** How many of the rows are of each kind, for a confirmation that counts the selection per kind. */
export function countByKind(
  rows: readonly Pick<PlannedOrder, "kind">[],
): Record<PlannedOrderKind, number> {
  const out: Record<PlannedOrderKind, number> = { manufacture: 0, purchase: 0, transfer: 0 };
  for (const r of rows) out[r.kind] += 1;
  return out;
}

/** The message as accept and dismiss compare it, from what the screen read. */
export function expectedOf(
  order: Pick<PlannedOrder, "action_message" | "action_message_expected">,
): ExpectedActionMessage | undefined {
  if (order.action_message_expected !== null) return order.action_message_expected;
  return undefined;
}

/** One row of the bulk result panel. */
export interface BulkOutcome {
  readonly id: string;
  readonly poNo: string;
  /** `firmed`, `accepted`, `cancelled` or `converted` when the write took; null when it was refused or not sent. */
  readonly result: "firmed" | "accepted" | "cancelled" | "converted" | null;
  readonly code?: string | undefined;
  readonly status?: number | undefined;
  /** The order's message now, on 409 `ACTION_MESSAGE_CHANGED`. */
  readonly currentMessage?: ExpectedActionMessage | null | undefined;
  /** The order's status now, on the two not-proposed and not-firmed refusals. */
  readonly orderStatus?: PlannedOrderStatus | undefined;
  /** True for a selected row the screen did not send, with the reason in `code`. */
  readonly notSent?: boolean | undefined;
  /** On a conversion: the document the order became, and whether it had become it before. */
  readonly convertedKind?: PlannedOrderConvertedKind | undefined;
  readonly convertedRefId?: string | undefined;
  readonly alreadyConverted?: boolean | undefined;
  /** On a refused conversion: the pin refused and why, or the product and date no routing was found for. */
  readonly pinReason?: "bom_archived" | "routing_archived" | "routing_without_steps" | undefined;
  readonly startDate?: string | undefined;
}

/** The server's per-id answers, joined to the numbers the screen read, in the server's order. */
export function outcomesOf(
  results: readonly PlannedOrderBulkResult[],
  poNoOf: (id: string) => string,
): BulkOutcome[] {
  return results.map((r) => ({
    id: r.id,
    poNo: poNoOf(r.id),
    result: r.result,
    code: r.refusal?.code,
    status: r.refusal?.status,
    currentMessage: r.refusal?.current_message,
    orderStatus: r.refusal?.order_status,
  }));
}

/** The client's code for a selected row a fan-out does not send because it is not firmed. */
export const NOT_FIRMED_LOCALLY = "PLANNED_ORDER_NOT_FIRMED";

/**
 * §4.5's cancel: no bulk route, so one request per selected firmed row with
 * the one reason, one at a time in id order. A selected row that is not
 * firmed is not sent and is listed as not firmed. `send` answers the code and
 * status of a refusal, or nothing when the cancel took.
 */
export async function cancelFanOut(
  rows: readonly Pick<PlannedOrder, "id" | "po_no" | "status">[],
  send: (
    id: string,
  ) => Promise<{ code?: string; status?: number; orderStatus?: PlannedOrderStatus } | undefined>,
): Promise<BulkOutcome[]> {
  const ordered = [...rows].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const out: BulkOutcome[] = [];
  for (const row of ordered) {
    if (row.status !== "firmed") {
      out.push({
        id: row.id,
        poNo: row.po_no,
        result: null,
        code: NOT_FIRMED_LOCALLY,
        orderStatus: row.status,
        notSent: true,
      });
      continue;
    }
    const refusal = await send(row.id);
    out.push(
      refusal === undefined
        ? { id: row.id, poNo: row.po_no, result: "cancelled" }
        : {
            id: row.id,
            poNo: row.po_no,
            result: null,
            code: refusal.code,
            status: refusal.status,
            orderStatus: refusal.orderStatus,
          },
    );
  }
  return out;
}

/** Ids in the order the server converts them, so the panel lists them as they ran. */
function byId<T extends { readonly id: string }>(a: T, b: T): number {
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * §4.5's Convert: one request carrying the selected firmed rows in id order. A
 * selected row that is not firmed is not sent and is listed as not firmed, as
 * the cancel lists it; a converted one is sent, because the server answers it
 * with its document as `ALREADY_CONVERTED`.
 */
export function convertSelection(rows: readonly Pick<PlannedOrder, "id" | "po_no" | "status">[]): {
  send: string[];
  notSent: BulkOutcome[];
} {
  const send: string[] = [];
  const notSent: BulkOutcome[] = [];
  for (const row of [...rows].sort(byId)) {
    if (row.status === "firmed" || row.status === "converted") send.push(row.id);
    else
      notSent.push({
        id: row.id,
        poNo: row.po_no,
        result: null,
        code: NOT_FIRMED_LOCALLY,
        orderStatus: row.status,
        notSent: true,
      });
  }
  return { send, notSent };
}

/** The conversion's per-id answers joined with the rows not sent, in id order. */
export function convertOutcomesOf(
  results: readonly PlannedOrderConvertResult[],
  notSent: readonly BulkOutcome[],
  poNoOf: (id: string) => string,
): BulkOutcome[] {
  const answered: BulkOutcome[] = results.map((r) => {
    if (r.status === "converted") {
      return {
        id: r.id,
        poNo: poNoOf(r.id),
        result: "converted",
        convertedKind: r.converted_kind ?? undefined,
        convertedRefId: r.converted_ref_id ?? undefined,
        alreadyConverted: r.code === "ALREADY_CONVERTED",
      };
    }
    return {
      id: r.id,
      poNo: poNoOf(r.id),
      result: null,
      code: r.refusal?.code ?? "other",
      status: r.refusal?.status,
      orderStatus: r.refusal?.order_status,
      pinReason: r.refusal?.reason,
      startDate: r.refusal?.start_date,
    };
  });
  return [...answered, ...notSent].sort(byId);
}

/** Whether accept may go in bulk: `reschedule_out` alone, the safe direction (§3.8). */
export function bulkAcceptable(kind: ActionMessageKind | null): boolean {
  return kind === "reschedule_out";
}

/** A short id for a reader with no name to show: the uuid's random tail, the
 * one rule the whole product prints (`lib/short-id.ts`). */
export function shortId(id: string): string {
  return shortIdTail(id);
}

// ---- the explanation drawer's waterfall and timeline (design-system §3.15) -----------

const SCALE = 1_000_000n;

/** A decimal string of at most six places as millionths, exactly. */
export function toMicro(value: string): bigint {
  const m = QTY.exec(value.trim());
  if (m === null) return 0n;
  const frac = (m[3] ?? "").slice(0, 6).padEnd(6, "0");
  const n = BigInt(m[2] ?? "0") * SCALE + BigInt(frac);
  return m[1] === "-" ? -n : n;
}

/** Millionths back to a decimal string, trailing zeros dropped. */
export function fromMicro(n: bigint): string {
  const negative = n < 0n;
  const abs = negative ? -n : n;
  const whole = (abs / SCALE).toString();
  const frac = (abs % SCALE).toString().padStart(6, "0").replace(/0+$/u, "");
  return `${negative ? "-" : ""}${whole}${frac === "" ? "" : `.${frac}`}`;
}

/** One row of the quantity waterfall: a label key, and the values before, the term and after. */
export interface WaterfallRow {
  readonly key: string;
  /** `planning.orders.waterfall.<label>` */
  readonly label: string;
  readonly params?: Readonly<Record<string, string | number>>;
  /** Index into `explanation.demand` for a demand item's own row. */
  readonly item?: number;
  readonly before?: string;
  /** The term the row applies; `op` says how. */
  readonly term?: string;
  readonly op?: "minus" | "percent" | "per" | "plain";
  readonly after?: string;
  readonly indent: 0 | 1 | 2;
  /** A total row, drawn heavier. */
  readonly total?: boolean;
  /** A row that is part of the term above it and is not subtracted again. */
  readonly partOf?: boolean;
}

function scrapLabel(step: NonNullable<ExplanationDemandItem["scrap_steps"]>[number]): string {
  return `scrap_${step.kind}`;
}

/**
 * The quantity math as rows (spec 03 §4.5, design-system §3.15): per demand
 * item its base and each scrap step with before and after, then gross, then
 * every netting term (a zero term is shown, not hidden) with the running
 * value, remnants by number under theirs and the off-cuts inside on-hand, then
 * net, lot sizing and the MPS cell. The running values are exact: every term
 * is a decimal of at most six places, summed in millionths.
 */
export function quantityWaterfall(explanation: PlannedOrderExplanation): WaterfallRow[] {
  const rows: WaterfallRow[] = [];
  explanation.demand.forEach((item, i) => {
    const steps = item.scrap_steps ?? [];
    rows.push({
      key: `item-${String(i)}`,
      label: "item",
      item: i,
      after: steps.length > 0 ? (item.base_qty ?? steps[0]?.before ?? item.qty) : item.qty,
      indent: 0,
    });
    steps.forEach((step, s) => {
      rows.push({
        key: `item-${String(i)}-step-${String(s)}`,
        label: scrapLabel(step),
        params: step.step_no !== undefined ? { step: step.step_no } : {},
        before: step.before,
        term: step.value,
        op: step.kind === "batch" ? "per" : "percent",
        after: step.after,
        indent: 1,
      });
    });
    if (steps.length > 0) {
      rows.push({
        key: `item-${String(i)}-total`,
        label: "item_needs",
        after: item.qty,
        indent: 1,
      });
    }
  });
  const q = explanation.qty_math;
  const n = q.net_of_on_hand;
  rows.push({ key: "gross", label: "gross", after: q.gross_requirement, indent: 0, total: true });
  let running = toMicro(q.gross_requirement);
  const subtract = (key: string, term: string) => {
    const before = running;
    running = before - toMicro(term);
    rows.push({
      key,
      label: key,
      before: fromMicro(before),
      term,
      op: "minus",
      after: fromMicro(running),
      indent: 0,
    });
  };
  subtract("remnants_matched", n.remnants_matched);
  n.remnants.forEach((r, i) => {
    rows.push({
      key: `remnant-${String(i)}`,
      label: r.length_m === null ? "remnant" : "remnant_length",
      params: { no: r.remnant_no, ...(r.length_m === null ? {} : { length: r.length_m }) },
      term: r.qty,
      op: "plain",
      indent: 1,
      partOf: true,
    });
  });
  subtract("on_hand", n.on_hand);
  rows.push({
    key: "offcuts_matched",
    label: "offcuts_matched",
    term: n.offcuts_matched,
    op: "plain",
    indent: 1,
    partOf: true,
  });
  rows.push({ key: "reserved", label: "reserved", term: n.reserved, op: "plain", indent: 1 });
  subtract("in_transit", n.in_transit);
  subtract("open_supply", n.open_supply);
  subtract("firmed", n.firmed);
  subtract("co_by_products", n.co_by_products);
  subtract("lot_excess_carried", n.lot_excess_carried);
  rows.push({ key: "net", label: "net", after: n.net, indent: 0, total: true });
  const lot = q.lot_sizing;
  rows.push({
    key: "lot_sizing",
    label: lot.method === "lot_for_lot" ? "lot_for_lot" : "min_multiple",
    params: {
      min: lot.min_lot_qty ?? "",
      multiple: lot.lot_multiple_qty ?? "",
    },
    before: lot.before,
    after: lot.after,
    indent: 0,
    total: true,
  });
  if (q.mps !== null) {
    rows.push({ key: "mps_cell", label: "mps_cell", after: q.mps.cell_qty, indent: 0 });
    rows.push({
      key: "mps_committed",
      label: "mps_committed",
      term: q.mps.committed,
      op: "plain",
      indent: 1,
    });
    rows.push({
      key: "mps_overflow",
      label: "mps_overflow",
      term: q.mps.overflow,
      op: "plain",
      indent: 1,
    });
    rows.push({
      key: "mps_excess",
      label: "mps_excess",
      term: q.mps.excess,
      op: "plain",
      indent: 1,
    });
  }
  return rows;
}

/** One row of the date timeline, read from due back to release. */
export interface TimelineRow {
  readonly key: string;
  /** `need`, `due`, an offset kind, `start` or `release`. */
  readonly kind: string;
  readonly date?: string;
  /** The date before the clamp moved it to today. */
  readonly original?: string;
  readonly days?: number;
  readonly data?: Readonly<Record<string, unknown>>;
  /** The closed dates this offset stepped over. */
  readonly skipped: readonly string[];
}

/**
 * The date math as rows from the need and the due date back to start and
 * release, each offset with the closed dates it stepped over (spec 03 §4.5).
 * A skipped date falls in the window its offset spans: the due snap from the
 * need back to due, the duration from due back to the start before any source
 * snap, the source snap across the start it moved, and the release buffer
 * from start back to release. A date no window holds stays under the release
 * row, so nothing the run skipped is left out.
 */
export function dateTimeline(dm: PlannedOrderExplanation["date_math"]): TimelineRow[] {
  const startUnclamped = dm.original_start_date ?? dm.start_date;
  const releaseUnclamped = dm.original_release_date ?? dm.release_date;
  const sourceSnap = dm.offsets.find((o) => o.kind === "source_snap");
  const snapFrom =
    sourceSnap !== undefined && typeof sourceSnap.data["from"] === "string"
      ? sourceSnap.data["from"]
      : undefined;
  const dueSnap = dm.offsets.find((o) => o.kind === "due_snap");
  const need =
    dueSnap !== undefined && typeof dueSnap.data["from"] === "string"
      ? dueSnap.data["from"]
      : undefined;
  const left = new Set(dm.skipped_dates);
  const take = (inside: (d: string) => boolean): string[] => {
    const out = [...left].filter(inside).sort().reverse();
    for (const d of out) left.delete(d);
    return out;
  };
  const rows: TimelineRow[] = [];
  if (need !== undefined) rows.push({ key: "need", kind: "need", date: need, skipped: [] });
  rows.push({ key: "due", kind: "due", date: dm.due_date, skipped: [] });
  dm.offsets.forEach((o, i) => {
    let skipped: string[] = [];
    if (o.kind === "due_snap" && need !== undefined)
      skipped = take((d) => d > dm.due_date && d <= need);
    else if (o.kind === "routing_time" || o.kind === "lead_time_days")
      skipped = take((d) => d >= (snapFrom ?? startUnclamped) && d < dm.due_date);
    else if (o.kind === "source_snap" && snapFrom !== undefined)
      skipped = take((d) => d >= startUnclamped && d < snapFrom);
    else if (o.kind === "release_buffer")
      skipped = take((d) => d >= releaseUnclamped && d < startUnclamped);
    rows.push({ key: `offset-${String(i)}`, kind: o.kind, days: o.days, data: o.data, skipped });
  });
  rows.push({
    key: "start",
    kind: "start",
    date: dm.start_date,
    ...(dm.original_start_date !== undefined && dm.original_start_date !== dm.start_date
      ? { original: dm.original_start_date }
      : {}),
    skipped: [],
  });
  rows.push({
    key: "release",
    kind: "release",
    date: dm.release_date,
    ...(dm.original_release_date !== undefined && dm.original_release_date !== dm.release_date
      ? { original: dm.original_release_date }
      : {}),
    skipped: [...left].sort().reverse(),
  });
  return rows;
}
